import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Hawk Eye — FTUE demo (3 essais).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SECTOR_ANGLES, TARGET_SYMBOLS, } from './params';
const RADIUS = 110;
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'hawk_eye.demo.step1_title',
        hintKey: 'hawk_eye.demo.step1_hint',
        target: '◆',
        targetAngle: 0,
        distractors: [],
        durationMs: 1500,
    },
    {
        idx: 2,
        titleKey: 'hawk_eye.demo.step2_title',
        hintKey: 'hawk_eye.demo.step2_hint',
        target: '◆',
        targetAngle: 135,
        distractors: [
            { angle: 0, symbol: '●' },
            { angle: 270, symbol: '▲' },
        ],
        durationMs: 1500,
    },
    {
        idx: 3,
        titleKey: 'hawk_eye.demo.step3_title',
        hintKey: 'hawk_eye.demo.step3_hint',
        target: '★',
        targetAngle: 225,
        distractors: [
            { angle: 0, symbol: '◆' },
            { angle: 90, symbol: '●' },
            { angle: 180, symbol: '▲' },
        ],
        durationMs: 1000,
    },
];
const FIELD = 280;
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('showing');
    const [pick, setPick] = useState(null);
    const timerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const correct = pick === step.targetAngle;
    useEffect(() => {
        if (phase !== 'showing')
            return;
        timerRef.current = setTimeout(() => setPhase('awaiting'), step.durationMs);
        return () => {
            if (timerRef.current !== null)
                clearTimeout(timerRef.current);
        };
    }, [phase, step.durationMs]);
    const handlePick = (a) => {
        if (phase !== 'awaiting')
            return;
        setPick(a);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('showing');
        setPick(null);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('hawk_eye.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsxs("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: [t(step.hintKey), ' ', _jsx("span", { className: "font-display text-[16px] text-cyan-network", children: t('hawk_eye.demo.target_label', { symbol: step.target }) })] }), _jsxs("div", { className: "relative", style: { width: FIELD, height: FIELD, background: '#1a1a1a' }, children: [_jsx("div", { className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-display text-[28px] text-cyan-surface", children: "+" }), phase === 'showing' && (_jsxs(_Fragment, { children: [_jsx("div", { className: "absolute left-1/2 top-1/2 flex h-[40px] w-[40px] -translate-x-1/2 -translate-y-1/2 items-center justify-center text-[24px] text-cyan-surface", style: {
                                    transform: `translate(-50%, -50%) rotate(${step.targetAngle}deg) translateY(-${RADIUS}px) rotate(-${step.targetAngle}deg)`,
                                }, children: step.target }), step.distractors.map((d, i) => (_jsx("div", { className: "absolute left-1/2 top-1/2 flex h-[40px] w-[40px] -translate-x-1/2 -translate-y-1/2 items-center justify-center text-[24px] text-cyan-surface", style: {
                                    transform: `translate(-50%, -50%) rotate(${d.angle}deg) translateY(-${RADIUS}px) rotate(-${d.angle}deg)`,
                                }, children: d.symbol }, i)))] })), phase === 'awaiting' &&
                        SECTOR_ANGLES.map((a) => (_jsx("button", { type: "button", onClick: () => handlePick(a), "aria-label": t('hawk_eye.sector_aria', { angle: a }), className: "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[44px] w-[44px] rounded-full border-2 border-cyan-border bg-cyan-surface transition hover:scale-110 hover:bg-cyan-network/15", style: {
                                transform: `translate(-50%, -50%) rotate(${a}deg) translateY(-${RADIUS}px) rotate(-${a}deg)`,
                            } }, a))), phase === 'feedback' && (_jsx("div", { className: "flex h-full w-full items-center justify-center", children: _jsx("span", { className: [
                                'font-display text-[36px]',
                                correct ? 'text-cyan-core' : 'text-cyan-media',
                            ].join(' '), children: correct ? '✓' : '✗' }) }))] }), _jsx("p", { className: "sr-only", children: t('hawk_eye.demo.targets_aria', {
                    symbol: step.target,
                    list: TARGET_SYMBOLS.join(', '),
                }) }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map